/** @jsx jsx */
import React, { useMemo } from 'react';
import { css, jsx } from '@compiled/react';
import Avatar from '@atlaskit/avatar';
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
// TODO HL-1430 move these utils into our package once JWM business page is replaced with ours
import {
	transformTitle,
	transformContent,
} from '@atlassian/jira-business-summary/src/ui/summary-view/activity-feed/activity-item/utils.tsx';
import { formatDistanceWithLocale } from '@atlassian/jira-platform-utils-date-fns/src/main.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import type { ActivityFeedItem } from '../types.tsx';

type ActivityItemProps = {
	item: ActivityFeedItem;
	onClick: (target: 'user' | 'issue') => void;
};

export const ActivityItem = ({ item, onClick }: ActivityItemProps) => {
	const locale = useLocale();

	const linksTarget = '_blank';

	const title = useMemo(() => transformTitle(item, onClick, linksTarget), [item, onClick]);
	const content = useMemo(() => transformContent(item), [item]);

	const relativePublishedTime = useMemo(
		() => formatDistanceWithLocale(new Date(item.published), new Date(), locale, true),
		[item, locale],
	);

	return (
		<Box xcss={wrapperStyles} as="li">
			<Flex>
				<Avatar src={item.author.avatarUrl} name={item.author.name} size="small" />
				<div css={titleContainerStyles}>
					{title}
					<Text color="color.text.subtle" as="p">
						{relativePublishedTime}
					</Text>
				</div>
			</Flex>

			{content}
		</Box>
	);
};

const wrapperStyles = xcss({
	position: 'relative',
	margin: 'space.0',
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
	paddingLeft: 'space.0',
	paddingRight: 'space.0',
});

const titleContainerStyles = css({
	marginLeft: token('space.100', '8px'),
	// TODO HL-1430 remove these styles and update inside the transformTitle util function once
	// JWM business page is replaced with ours and the util functions get moved to our package
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	a: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		textDecoration: 'none !important',
		'&:hover': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			textDecoration: 'underline !important',
		},
	},
});
